import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { View, Text, Pressable, ScrollView } from '../../lib/react-native-compat';
import { jobUserDataAtom, jobPhaseCodesAtom, jobCostDetailAtom } from '../../store/company';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import useQtyHistory from './useQtyHistory';
import { usePhaseMetrics, evenPeriodSpread } from './usePhaseMetrics';
import { ScheduleModal } from './PhaseToolModals';

// Phase quick-pick chips for the Work Performed section. The list is scoped to
// the job: phases that appear on the job's cost detail plus any phase on the
// phased Burn Tool schedule. Phases whose Burn Tool start/stop interval
// contains the report date are suggested up front, with the schedule dates
// shown on the chip. A search box filters everything; searching also surfaces
// dictionary codes not yet on the job in case a new phase needs to be added.
// Toggling a chip on adds a work row prefilled with the cost code; toggling
// off removes that phase's rows unless the user already typed a description.

// Summary/rollup codes that never represent field work.
const EXCLUDED_CODES = { 1000: 1, 899998: 1, 990000: 1 };

const dateOf = (v) => (v ? moment(v).add(6, 'hours').format('YYYY-MM-DD') : null);

const numOf = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

// A work row the user hasn't typed into yet (beyond what the chip prefilled).
const rowIsUntouched = (r) => !r.description;

const chipBase = {
    borderRadius: 10,
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    marginBottom: 6,
    cursor: 'pointer',
    width: '49%',
    boxSizing: 'border-box',
};

const Chip = ({ code, description, selected, onToggle, schedule }) => (
    <Pressable
        onPress={onToggle}
        title={`${code} — ${description}`}
        style={{
            ...chipBase,
            backgroundColor: selected ? '#1D4ED8' : '#fff',
            border: selected ? '1px solid #1D4ED8' : '1px solid #CBD5E1',
        }}
    >
        <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <Text style={{ fontSize: 12, fontWeight: '700', color: selected ? '#fff' : '#334155' }}>{code}</Text>
            {description ? (
                <Text
                    numberOfLines={1}
                    style={{ fontSize: 12, color: selected ? '#DBEAFE' : '#64748B', marginLeft: 6, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {description}
                </Text>
            ) : <View style={{ flex: 1 }} />}
            <Text style={{ fontSize: 12, fontWeight: '700', color: selected ? '#fff' : '#94A3B8', marginLeft: 8 }}>
                {selected ? '✓' : '+'}
            </Text>
        </View>
        {schedule ? (
            <Text style={{ fontSize: 10, color: selected ? '#BFDBFE' : '#94A3B8', marginTop: 2 }}>
                {schedule}
            </Text>
        ) : null}
    </Pressable>
);

const ChipGrid = ({ children }) => (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        {children}
    </View>
);

// '(Schedule|Burn Tool) 7/15 – 8/2' label for a phase's schedule interval —
// shared with the work tables so their cost-code cells show the same dates as
// the chips. The source tag says where the window came from.
export const burnLabel = (intervals, code) => {
    const iv = intervals?.[String(code || '').trim()];
    if (!iv) return null;
    const src = iv.source === 'schedule' ? 'Schedule' : 'Burn Tool';
    return `${src} ${moment(iv.start).format('M/D')} – ${moment(iv.end).format('M/D')}`;
};

// Job-scoped phase list shared by the Work chips and every cost-code picker
// (the fr-costcodes datalist in index.js): phases on the job's cost detail
// plus anything on the phased Burn Tool schedule, rollup codes excluded.
// Descriptions come from cost detail first, then the global dictionary.
//
// Pass the step's qtyHist to overlay the saved quantity schedule's windows:
// a phase with a planned curve gets its schedule dates (source 'schedule')
// instead of the Burn Tool interval, which is what makes the suggested chips,
// Gantt bars and status reads follow the weekly plan. Callers without it keep
// the Burn-Tool-only behavior.
export const useJobPhases = (qtyHist = null) => {
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);

    // Start/stop intervals from the phased Burn Tool rows.
    const burnIntervals = useMemo(() => {
        const out = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab !== 'Burn Tool Labor Phased') return;
            if (!d.Phase_Start || !d.Phase_End) return;
            const code = String(d.Forecast_Row_ID || '').split('=')[0].trim();
            if (!code || out[code]) return;
            out[code] = { start: dateOf(d.Phase_Start), end: dateOf(d.Phase_End), source: 'burn' };
        });
        return out;
    }, [jobUserData]);

    const planned = qtyHist?.planned;
    const intervals = useMemo(() => {
        if (!planned || Object.keys(planned).length === 0) return burnIntervals;
        const out = { ...burnIntervals };
        Object.entries(planned).forEach(([code, p]) => {
            if (p?.start && p?.end) out[code] = { start: p.start, end: p.end, source: 'schedule' };
        });
        return out;
    }, [burnIntervals, planned]);

    const jobPhases = useMemo(() => {
        const out = {};
        (jobCostDetail || []).forEach((row) => {
            const code = String(row?.Phase_Code || '').trim();
            if (!code || EXCLUDED_CODES[code] || out[code]) return;
            out[code] = row.Phase_Code_Description || phaseCodes?.[code] || '';
        });
        Object.keys(intervals).forEach((code) => {
            if (!EXCLUDED_CODES[code] && !(code in out)) out[code] = phaseCodes?.[code] || '';
        });
        return out;
    }, [jobCostDetail, intervals, phaseCodes]);

    return { jobPhases, intervals };
};

const WorkPhaseChips = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [showAll, setShowAll] = useState(false);
    // The B/E/A day rows triple each Gantt lane's height — collapsed by
    // default so the timeline stays slim; the bars always show.
    const [showDetail, setShowDetail] = useState(false);
    const [filter, setFilter] = useState('');
    // Schedule of quantities per phase: latest budget qty, installed to date,
    // per-day submissions from prior reports and the saved quantity schedule's
    // planned curve; unit from the tracker module registry.
    const qtyHist = useQtyHistory(report);
    const { jobPhases, intervals } = useJobPhases(qtyHist);
    // The job-wide schedule view open over the picker.
    const [showSchedule, setShowSchedule] = useState(false);
    // Gantt data rows show quantities or crew hours; quantities by default.
    const [ganttMode, setGanttMode] = useState('qty');
    // JDE actuals (labor hours + HeavyJob FP quantities) and phased Burn Tool
    // budget hours — shared with the phase time-card cards.
    const { actualHours, fpQty, phasedHours } = usePhaseMetrics();

    const reportDate = report?.reportDate || moment().format('YYYY-MM-DD');

    // Today's in-progress entries (time card hours, qty installed) show live
    // in the Actual row — prior-report history excludes the open report.
    const liveQty = {};
    (report?.sections?.quantities || []).forEach((r) => {
        const c = String(r?.costCode || '').trim();
        const q = numOf(r?.installedToday);
        if (c && q) liveQty[c] = (liveQty[c] || 0) + q;
    });
    const liveHours = {};
    (report?.sections?.timeCard || []).forEach((r) => {
        Object.entries(r?.hours || {}).forEach(([c, h]) => {
            const v = numOf(h);
            if (v) liveHours[c] = (liveHours[c] || 0) + v;
        });
    });
    const rows = report?.sections?.work || [];
    const selectedCodes = new Set(rows.map((r) => String(r.costCode || '').trim()).filter(Boolean));

    const jobCodes = Object.keys(jobPhases);
    const descOf = (c) => jobPhases[c] ?? phaseCodes?.[c] ?? '';

    // Suggested = Burn Tool interval contains the report date. Sorted by start date, then code.
    const suggested = jobCodes
        .filter((c) => {
            const iv = intervals[c];
            return iv && iv.start && iv.end && iv.start <= reportDate && reportDate <= iv.end;
        })
        .sort((a, b) => (intervals[a].start < intervals[b].start ? -1 : intervals[a].start > intervals[b].start ? 1 : a.localeCompare(b)));

    const suggestedSet = new Set(suggested);
    const others = jobCodes.filter((c) => !suggestedSet.has(c)).sort((a, b) => a.localeCompare(b));

    const filterLc = filter.trim().toLowerCase();
    const matches = (c) => !filterLc || c.toLowerCase().includes(filterLc) || String(descOf(c)).toLowerCase().includes(filterLc);
    const othersFiltered = others.filter(matches);

    // Searching can also pull in dictionary codes not on this job yet.
    const jobSet = new Set(jobCodes);
    const dictionaryFiltered = filterLc
        ? Object.keys(phaseCodes || {})
            .filter((c) => !jobSet.has(c) && !EXCLUDED_CODES[c] && matches(c))
            .sort((a, b) => a.localeCompare(b))
            .slice(0, 30)
        : [];

    if (!report || (suggested.length === 0 && jobCodes.length === 0)) return null;

    const setRows = (next) => {
        setReport({ ...report, sections: { ...report.sections, work: next } });
        setDirty(true);
    };

    const toggle = (code) => {
        if (readOnly) return;
        if (selectedCodes.has(code)) {
            // Deselect: only drop rows for this phase the user hasn't filled in.
            const next = rows.filter((r) => String(r.costCode || '').trim() !== code || !rowIsUntouched(r));
            if (next.length !== rows.length) setRows(next);
        } else {
            setRows([...rows, { costCode: code }]);
        }
    };

    const scheduleLabel = (c) => burnLabel(intervals, c);

    // Gantt timeline for the phases scheduled over the report date. Each bar is
    // the phase's Burn Tool interval and toggles the work row exactly like a
    // chip. Fixed daily window: 2 weeks back, 2 weeks ahead of the report
    // date, with a gridline for every day.
    const renderGantt = (codes) => {
        if (codes.length === 0) return null;
        const rd = moment(reportDate);
        const winStart = rd.clone().subtract(14, 'days');
        const winEnd = rd.clone().add(15, 'days');
        const totalDays = Math.max(winEnd.diff(winStart, 'days'), 1);
        // A small left gutter holds the B/E/A row tags; the timeline maps into
        // the remaining width so tags never sit on top of the first day column.
        const GUT = 3;
        const pctRaw = (d) => Math.min(100, Math.max(0, (moment(d).diff(winStart, 'days') / totalDays) * 100));
        const pct = (d) => GUT + (pctRaw(d) * (100 - GUT)) / 100;

        const days = [];
        for (let t = winStart.clone(); t.isBefore(winEnd); t.add(1, 'days')) days.push(t.clone());
        const todayPct = pct(rd);

        const AXIS_H = 36;
        const dayPct = (100 - GUT) / totalDays;
        return (
            <View style={{ position: 'relative', marginBottom: 6 }}>
                {/* weekend shading: full-height gray band per Sat/Sun column */}
                {days.filter((t) => t.day() === 0 || t.day() === 6).map((t) => (
                    <View key={`w${t.format('YYYY-MM-DD')}`} style={{ position: 'absolute', left: `${pct(t)}%`, width: `${dayPct}%`, top: AXIS_H, bottom: 0, backgroundColor: 'rgba(148, 163, 184, 0.14)', zIndex: 0 }} />
                ))}
                {/* date axis: every day labeled (Tue 7/21…), angled so they fit */}
                <View style={{ position: 'relative', height: AXIS_H }}>
                    {days.filter((t) => !t.isSame(rd, 'day')).map((t) => (
                        <Text
                            key={t.format('YYYY-MM-DD')}
                            style={{
                                position: 'absolute',
                                left: `${pct(t)}%`,
                                bottom: 2,
                                transform: 'rotate(-52deg)',
                                transformOrigin: 'left bottom',
                                fontSize: 9,
                                color: t.day() === 0 || t.day() === 6 ? '#CBD5E1' : '#94A3B8',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t.format('ddd M/D')}
                        </Text>
                    ))}
                    {/* today label: same angle/anchor as the rest, amber highlight */}
                    <Text style={{ position: 'absolute', left: `${todayPct}%`, bottom: 2, transform: 'rotate(-52deg)', transformOrigin: 'left bottom', fontSize: 9, fontWeight: '700', color: '#B45309', backgroundColor: '#FEF3C7', paddingLeft: 4, paddingRight: 4, borderRadius: 4, zIndex: 2, whiteSpace: 'nowrap' }}>
                        {rd.format('ddd M/D')}
                    </Text>
                </View>
                {/* per-day column lines */}
                {days.map((t) => (
                    <View key={`g${t.format('YYYY-MM-DD')}`} style={{ position: 'absolute', left: `${pct(t)}%`, top: AXIS_H, bottom: 0, width: 1, backgroundColor: t.day() === 0 || t.day() === 6 ? '#E8EDF4' : '#EEF2F7', zIndex: 0 }} />
                ))}
                {/* today marker */}
                <View style={{ position: 'absolute', left: `${todayPct}%`, top: AXIS_H, bottom: 0, width: 2, backgroundColor: '#F59E0B', borderRadius: 1, zIndex: 1 }} />
                {/* one lane per phase: bar + Budget / Est / Actual day rows */}
                {codes.map((c) => {
                    const iv = intervals[c];
                    const left = pct(iv.start);
                    const width = Math.max(pct(moment(iv.end).add(1, 'days')) - left, 2);
                    const selected = selectedCodes.has(c);
                    const labelInside = width >= 32;
                    const desc = descOf(c);

                    // Daily values for the three data rows. Budget (baseline)
                    // follows the saved quantity schedule's per-day curve when
                    // the phase has one, otherwise spreads the total evenly
                    // across the Burn Tool periods (ISO weeks) of the interval;
                    // Est (updated) is the run rate still needed to finish the
                    // remaining budget by the interval end; Actual is what was
                    // submitted each day (plus this report's live entries).
                    const isQty = ganttMode === 'qty';
                    const plan = isQty ? qtyHist.planFor(c) : null;
                    const budgetTotal = isQty
                        ? (qtyHist.budgets[c] || 0)
                        : (phasedHours[c] || []).reduce((t, r) =>
                            t + r.daily * (moment(r.end).diff(moment(r.start), 'days') + 1), 0);
                    const qtySpread = isQty && !plan ? evenPeriodSpread(budgetTotal, iv) : null;
                    const budgetOf = (d) => {
                        if (isQty) return plan ? numOf(plan.perDay[d]) : (qtySpread?.perDay[d] || 0);
                        return (phasedHours[c] || []).reduce((t, r) => t + (d >= r.start && d <= r.end ? r.daily : 0), 0);
                    };
                    // Quantities actuals: field-report submissions + HeavyJob
                    // FP postings from JDE (jobs still on HeavyJob time cards
                    // show real installs without any report history).
                    const dailyQty = {};
                    (qtyHist.daily?.[c] || []).forEach((d) => { dailyQty[d.date] = (dailyQty[d.date] || 0) + d.qty; });
                    Object.entries(fpQty[c] || {}).forEach(([d, v]) => { dailyQty[d] = (dailyQty[d] || 0) + v; });
                    const actualOf = (d) => {
                        let v = isQty ? (dailyQty[d] || 0) : (actualHours[c]?.[d] || 0);
                        if (d === reportDate) v += isQty ? (liveQty[c] || 0) : (liveHours[c] || 0);
                        return v;
                    };
                    const doneToDate = isQty
                        ? Object.entries(dailyQty).reduce((t, [d, v]) => t + (d <= reportDate ? v : 0), 0)
                            + (liveQty[c] || 0)
                        : Object.entries(actualHours[c] || {}).reduce((t, [d, v]) => t + (d <= reportDate ? v : 0), 0)
                            + (liveHours[c] || 0);
                    const estStart = reportDate > iv.start ? reportDate : iv.start;
                    const remDays = moment(iv.end).diff(moment(estStart), 'days') + 1;
                    const estDaily = budgetTotal > 0 && remDays > 0
                        ? Math.max(0, budgetTotal - doneToDate) / remDays
                        : 0;
                    const estOf = (d) => (estDaily && d >= estStart && d <= iv.end ? estDaily : 0);

                    const fmtCell = (v) => {
                        if (!v) return '';
                        const r = v >= 100 ? Math.round(v) : Math.round(v * 10) / 10;
                        return r >= 1000 ? Math.round(r).toLocaleString() : String(r);
                    };
                    const dataRow = (tag, color, top, valueOf) => (
                        <View style={{ position: 'absolute', left: 0, right: 0, top, height: 11, flexDirection: 'row', zIndex: 1 }}>
                            <Text style={{ width: `${GUT}%`, flexShrink: 0, fontSize: 8, lineHeight: '11px', fontWeight: '700', color, boxSizing: 'border-box' }}>{tag}</Text>
                            {days.map((t) => {
                                const key = t.format('YYYY-MM-DD');
                                return (
                                    <Text key={key} style={{ width: `${dayPct}%`, flexShrink: 0, fontSize: 8.5, lineHeight: '11px', textAlign: 'center', color, whiteSpace: 'nowrap', overflow: 'hidden', boxSizing: 'border-box' }}>
                                        {fmtCell(valueOf(key))}
                                    </Text>
                                );
                            })}
                        </View>
                    );

                    return (
                        <View key={c} style={{ position: 'relative', height: showDetail ? 56 : 24, borderBottom: '1px solid #F1F5F9' }}>
                            <Pressable
                                onPress={() => toggle(c)}
                                title={`${c} — ${desc}\n${scheduleLabel(c)}`}
                                style={{
                                    position: 'absolute',
                                    left: `${left}%`,
                                    width: `${width}%`,
                                    top: 1,
                                    height: 20,
                                    borderRadius: 6,
                                    backgroundColor: selected ? '#1D4ED8' : '#fff',
                                    border: selected ? '1px solid #1D4ED8' : '1px solid #CBD5E1',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    paddingLeft: 6,
                                    paddingRight: 6,
                                    overflow: 'hidden',
                                    zIndex: 2,
                                }}
                            >
                                <Text style={{ fontSize: 11, fontWeight: '700', color: selected ? '#fff' : '#334155', whiteSpace: 'nowrap' }}>{c}</Text>
                                {labelInside && desc ? (
                                    <Text style={{ fontSize: 11, color: selected ? '#DBEAFE' : '#64748B', marginLeft: 5, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {desc}
                                    </Text>
                                ) : <View style={{ flex: 1 }} />}
                                <Text style={{ fontSize: 11, fontWeight: '700', color: selected ? '#fff' : '#94A3B8', marginLeft: 4 }}>
                                    {selected ? '✓' : '+'}
                                </Text>
                            </Pressable>
                            {!labelInside && desc ? (
                                left + width < 65 ? (
                                    <Text style={{ position: 'absolute', left: `calc(${left + width}% + 6px)`, top: 4, fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', maxWidth: '34%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {desc}
                                    </Text>
                                ) : (
                                    <Text style={{ position: 'absolute', right: `calc(${100 - left}% + 6px)`, top: 4, fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', maxWidth: '34%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {desc}
                                    </Text>
                                )
                            ) : null}
                            {/* Budget (baseline) / Est (updated) / Actual day rows */}
                            {showDetail && dataRow('B', '#94A3B8', 22, budgetOf)}
                            {showDetail && dataRow('E', '#2563EB', 33, estOf)}
                            {showDetail && dataRow('A', '#0F172A', 44, actualOf)}
                        </View>
                    );
                })}
            </View>
        );
    };

    const renderChips = (codes) => (
        <ChipGrid>
            {codes.map((c) => (
                <Chip
                    key={c}
                    code={c}
                    description={descOf(c)}
                    schedule={scheduleLabel(c)}
                    selected={selectedCodes.has(c)}
                    onToggle={() => toggle(c)}
                />
            ))}
        </ChipGrid>
    );

    return (
        <View style={{ borderRadius: 10, border: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', padding: 10, marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 6, marginBottom: 4 }}>
                <View style={{ flexShrink: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {suggested.length > 0 ? 'Phases scheduled this day — tap all that apply' : 'Job phases — tap all that apply'}
                    </Text>
                    {/* Legend for the B/E/A day rows — under the title so it
                        costs no extra line of its own. */}
                    {suggested.length > 0 && showDetail && (
                        <Text style={{ fontSize: 10, color: '#94A3B8' }}>
                            {ganttMode === 'qty'
                                ? 'Per day: B budget · E est. to finish · A actual installed'
                                : 'Per day: B budget · E est. to finish · A actual crew hours'}
                        </Text>
                    )}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 0, marginLeft: 8, columnGap: 6 }}>
                    {/* The job-wide schedule opens as a modal — field users are
                        on phones/iPads, so no jumping to another page. */}
                    <Pressable
                        onPress={() => setShowSchedule(true)}
                        title="Open the job schedule — every phase, its bar and its quantities"
                        style={{ flexDirection: 'row', alignItems: 'center', border: '1px solid #CBD5E1', borderRadius: 8, paddingTop: 4, paddingBottom: 4, paddingLeft: 8, paddingRight: 8, backgroundColor: '#fff', cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#475569' }}>Schedule</Text>
                    </Pressable>
                    {/* Quantities | Hours — what the B/E/A rows show */}
                    {suggested.length > 0 && showDetail && (
                        <View style={{ flexDirection: 'row', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden' }}>
                            {[['qty', 'Quantities'], ['hours', 'Hours']].map(([m, label]) => (
                                <Pressable
                                    key={m}
                                    onPress={() => setGanttMode(m)}
                                    style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10, backgroundColor: ganttMode === m ? '#1D4ED8' : '#fff', cursor: 'pointer' }}
                                >
                                    <Text style={{ fontSize: 11, fontWeight: '700', color: ganttMode === m ? '#fff' : '#475569' }}>{label}</Text>
                                </Pressable>
                            ))}
                        </View>
                    )}
                </View>
            </View>
            {suggested.length > 0 && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ width: '100%' }}>
                    {/* overflow hidden clips the rotated date labels' tails at the
                        right edge — without it they extend past the timeline and
                        make the ScrollView scrollable even on wide screens. */}
                    <View style={{ minWidth: 720, width: '100%', overflow: 'hidden' }}>
                        {renderGantt(suggested)}
                    </View>
                </ScrollView>
            )}
            {/* The B/E/A day rows under each bar fold away by default. */}
            {suggested.length > 0 && (
                <Pressable
                    onPress={() => setShowDetail(!showDetail)}
                    style={{ alignSelf: 'flex-start', cursor: 'pointer', paddingTop: 4, paddingBottom: 2 }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>
                        {showDetail ? '− Hide daily budget / est / actual' : '+ Daily budget / est / actual'}
                    </Text>
                </Pressable>
            )}
            {suggested.length === 0 && (
                <Text style={{ fontSize: 12, color: '#94A3B8', marginBottom: 6 }}>
                    No phases are scheduled over this date — pick from the job phases below.
                </Text>
            )}

            {others.length > 0 && (
                <Pressable
                    onPress={() => {
                        if (showAll) setFilter('');
                        setShowAll(!showAll);
                    }}
                    style={{ alignSelf: 'flex-start', cursor: 'pointer', paddingTop: 4, paddingBottom: 2 }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>
                        {showAll ? '− Hide other job phases' : `+ Other job phases (${others.length})`}
                    </Text>
                </Pressable>
            )}
            {showAll && others.length > 0 && (
                <>
                    {/* Search lives inside the collapsed section — it filters
                        these chips and can surface dictionary codes not on the
                        job yet. */}
                    <div style={{ position: 'relative', maxWidth: 360, marginTop: 4, marginBottom: 4 }}>
                        <input
                            type="text"
                            value={filter}
                            placeholder="Search phases…"
                            onChange={(e) => setFilter(e.target.value)}
                            style={{ width: '100%', fontSize: 13, padding: '6px 30px 6px 10px', border: '1px solid #CBD5E1', borderRadius: 8, boxSizing: 'border-box' }}
                        />
                        {filter !== '' && (
                            <button
                                onClick={() => setFilter('')}
                                title="Clear search"
                                style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#64748B', fontSize: 14, lineHeight: 1, cursor: 'pointer', padding: '4px 8px' }}
                            >✕</button>
                        )}
                    </div>
                    <View style={{ marginTop: 2, maxHeight: 320, overflowY: 'auto' }}>
                        {renderChips(othersFiltered)}
                        {othersFiltered.length === 0 && dictionaryFiltered.length === 0 && (
                            <Text style={{ fontSize: 12, color: '#94A3B8' }}>No phases match the search.</Text>
                        )}
                    </View>
                    {dictionaryFiltered.length > 0 && (
                        <View style={{ marginTop: 6 }}>
                            <Text style={{ fontSize: 10, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                Not on this job — full phase list
                            </Text>
                            {renderChips(dictionaryFiltered)}
                        </View>
                    )}
                </>
            )}

            {/* Job-wide schedule + productivity, in place */}
            {showSchedule && (
                <ScheduleModal
                    onClose={() => setShowSchedule(false)}
                    intervals={intervals}
                    descOf={descOf}
                    reportDate={reportDate}
                    qtyHist={qtyHist}
                    actualHours={actualHours}
                    fpQty={fpQty}
                    phasedHours={phasedHours}
                />
            )}
        </View>
    );
};

export default WorkPhaseChips;
